import { useEffect, useMemo, useState } from "react";
import api, { extractErr } from "@/lib/api";
import { toast } from "sonner";
import { useStoreBase } from "@/lib/store";
import FortuneWheelDisk from "@/components/FortuneWheelDisk";
import { X, ShieldCheck } from "lucide-react";

export default function FortuneWheelWidget({ config }) {
  const { slug } = useStoreBase();
  const wheel = config.fortune_wheel;
  const slices = wheel?.slices || [];
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [turn, setTurn] = useState(0);
  const [phone, setPhone] = useState("");
  const storageKey = `fw:${slug}`;
  const storeName = config.store_info?.name || config.tenant?.name || "";

  useEffect(() => {
    if (!config.fortune_wheel_active || !slices.length) return;
    const seen = sessionStorage.getItem(`${storageKey}:dismissed`);
    if (!seen) setTimeout(() => setOpen(true), 800);
  }, [config.fortune_wheel_active, slices.length, storageKey]);

  const visitorKey = useMemo(() => {
    const k = `fw-visitor:${slug}`;
    let v = localStorage.getItem(k);
    if (!v) {
      v = crypto.randomUUID ? crypto.randomUUID() : String(Date.now());
      localStorage.setItem(k, v);
    }
    return v;
  }, [slug]);

  if (!config.fortune_wheel_active || !slices.length) return null;
  const n = slices.length;
  const angle = 360 / n;

  const phoneDigits = phone.replace(/\D/g, "").replace(/^(91|0)/, "");
  const phoneOk = /^[6-9]\d{9}$/.test(phoneDigits);

  const spin = async () => {
    if (busy) return;
    if (!phoneOk) {
      toast.error("Enter a valid 10-digit mobile number");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/store/${slug}/fortune-wheel/spin`, { visitor_key: visitorKey, phone: phoneDigits });
      const idx = Number(data.index) || 0;
      const extra = 360 * 6;
      const target = extra + (360 - (idx * angle + angle / 2));
      setTurn(target);
      setTimeout(() => setResult(data), 4200);
    } catch (e) {
      toast.error(extractErr(e));
      setBusy(false);
    }
  };

  const close = () => {
    setOpen(false);
    sessionStorage.setItem(`${storageKey}:dismissed`, "1");
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => { setOpen(true); setResult(null); setBusy(false); }}
        className="fixed bottom-24 right-4 z-40 rounded-full shadow-lg text-white text-sm font-semibold px-4 h-11"
        style={{ background: config.branding?.primary_color || "#3D2B1F" }}
        data-testid="fortune-wheel-fab"
      >
        Spin & win
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-[2px] flex items-end sm:items-center justify-center p-3 sm:p-6" data-testid="fortune-wheel-modal">
      <div className="relative w-full max-w-[860px] bg-white rounded-[28px] overflow-hidden shadow-2xl grid md:grid-cols-2">
        <button
          type="button"
          onClick={close}
          className="absolute right-3 top-3 z-30 w-8 h-8 rounded-full border border-stone-200 bg-white/90 text-stone-500 flex items-center justify-center hover:bg-stone-50"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
        <div
          className="relative flex items-center justify-center py-10 px-4 min-h-[340px]"
          style={{
            background: "radial-gradient(circle at 50% 40%, #fff7f2 0%, #f8d7dc 55%, #f3c4c8 100%)",
          }}
        >
          <div className="pointer-events-none absolute inset-0 opacity-50" style={{
            backgroundImage: "radial-gradient(#f59e0b 1.2px, transparent 1.2px), radial-gradient(#38bdf8 1.1px, transparent 1.1px), radial-gradient(#fb7185 1px, transparent 1px)",
            backgroundSize: "28px 28px, 36px 36px, 22px 22px",
            backgroundPosition: "0 0, 12px 18px, 20px 8px",
          }} />
          <FortuneWheelDisk slices={slices} turn={turn} spinning={busy || Boolean(result)} size={280} hubLabel="SPIN" />
        </div>
        <div className="bg-white px-6 sm:px-10 py-8 sm:py-12 flex flex-col justify-center text-center">
          {storeName ? (
            <div className="text-[11px] tracking-[0.28em] uppercase text-stone-400 font-semibold mb-3">{storeName}</div>
          ) : null}
          <h2 className="font-display text-2xl sm:text-[28px] font-bold text-stone-900 leading-tight">
            {wheel.headline || "Register To Win A Reward"}
          </h2>
          <p className="text-sm text-stone-500 mt-2">{wheel.subhead}</p>
          {result ? (
            <div className="mt-8 space-y-3" data-testid="fortune-wheel-result">
              <div className="font-display text-xl font-bold text-stone-900">{result.label}</div>
              {result.coupon_code ? (
                <div className="font-mono-nums text-lg tracking-wider bg-emerald-50 border border-emerald-200 rounded-xl py-2.5">{result.coupon_code}</div>
              ) : (
                <p className="text-sm text-stone-500">No coupon this time — try again later if the merchant allows it.</p>
              )}
              <button type="button" className="w-full h-12 rounded-lg bg-[#3D2B1F] hover:bg-[#2c1e16] text-white font-semibold tracking-wide uppercase text-sm" onClick={close}>
                Continue shopping
              </button>
            </div>
          ) : (
            <div className="mt-8 space-y-4">
              <label className="block text-left">
                <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">Mobile number</span>
                <input
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  required
                  maxLength={14}
                  placeholder="10-digit mobile number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  data-testid="fortune-wheel-phone"
                  className="mt-1 w-full h-12 rounded-lg border border-stone-200 px-3 text-base text-stone-900 outline-none focus:border-stone-400"
                />
              </label>
              <button
                type="button"
                className="w-full h-12 rounded-lg bg-[#3D2B1F] hover:bg-[#2c1e16] text-white font-semibold tracking-wide uppercase text-sm disabled:opacity-60"
                disabled={busy || !phoneOk}
                onClick={spin}
                data-testid="fortune-wheel-spin"
              >
                {busy ? "Spinning…" : (wheel.cta || "SPIN TO WIN")}
              </button>
              <div className="flex items-center justify-center gap-1.5 text-xs text-emerald-700">
                <ShieldCheck className="w-3.5 h-3.5" /> 100% Secured & Spam Free
              </div>
            </div>
          )}
          <button type="button" className="mt-5 text-xs text-stone-400 hover:text-stone-600" onClick={close}>Not now</button>
        </div>
      </div>
    </div>
  );
}
